import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

// Module-level constants - never recreated
const DEFAULT_LEAVE_SETTINGS = {
  default_annual_leave: 25,
  default_sick_leave: 10,
  default_carry_over_days: 0,
  allow_carry_over: false,
  entitled_to_bank_holidays: true,
  working_days: [1, 2, 3, 4, 5] // Mon-Fri default (ISO day numbers: 1=Mon, 7=Sun)
};

const DEFAULT_NEW_ENTITLEMENT = { name: '', description: '', default_days: 0 };
const DEFAULT_NEW_LEAVE_TYPE = { name: '', description: '', color: '#8B5CF6' };

/**
 * Hook to load and manage attendance settings data
 * Handles leave settings, custom entitlements, and leave types
 *
 * @param {string} companyId - Company ID
 * @returns {Object} - Settings data, stats, loading state, and actions
 */
export function useAttendanceSettings(companyId) {
  // Data state
  const [leaveSettings, setLeaveSettings] = useState(DEFAULT_LEAVE_SETTINGS);
  const [customEntitlements, setCustomEntitlements] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for request management
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  /**
   * Load all settings data from server
   * Uses Promise.all for parallel queries (already optimal)
   */
  const loadData = useCallback(async () => {
    if (!companyId) {
      console.log('[useAttendanceSettings] Missing companyId');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useAttendanceSettings] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useAttendanceSettings] Loading data with parallel queries...');
      console.time('[useAttendanceSettings] Load duration');

      // Parallel queries - already optimal pattern
      const [leaveSettingsResult, customEntitlementsResult, leaveTypesResult] = await Promise.all([
        supabase
          .from('company_leave_settings')
          .select('*')
          .eq('company_id', companyId)
          .single(),
        supabase
          .from('custom_entitlement_types')
          .select('*')
          .eq('company_id', companyId)
          .eq('is_active', true)
          .order('display_order', { ascending: true }),
        supabase
          .from('leave_request_types')
          .select('*')
          .or(`company_id.eq.${companyId},company_id.is.null`)
          .eq('is_active', true)
          .order('is_system_default', { ascending: false })
          .order('name', { ascending: true })
      ]);

      console.timeEnd('[useAttendanceSettings] Load duration');

      // Process leave settings
      if (!leaveSettingsResult.error && leaveSettingsResult.data) {
        setLeaveSettings({
          default_annual_leave: leaveSettingsResult.data.default_annual_leave || 25,
          default_sick_leave: leaveSettingsResult.data.default_sick_leave || 10,
          default_carry_over_days: leaveSettingsResult.data.default_carry_over_days || 0,
          allow_carry_over: leaveSettingsResult.data.default_carry_over_days > 0,
          entitled_to_bank_holidays: leaveSettingsResult.data.entitled_to_bank_holidays !== false,
          working_days: leaveSettingsResult.data.working_days || [1, 2, 3, 4, 5]
        });
      }

      // Process custom entitlements
      if (!customEntitlementsResult.error && customEntitlementsResult.data) {
        setCustomEntitlements(customEntitlementsResult.data);
      }

      // Process leave types
      if (!leaveTypesResult.error && leaveTypesResult.data) {
        setLeaveTypes(leaveTypesResult.data);
      }

      console.log('[useAttendanceSettings] Load complete:', {
        leaveSettings: !leaveSettingsResult.error,
        customEntitlements: customEntitlementsResult.data?.length || 0,
        leaveTypes: leaveTypesResult.data?.length || 0
      });

      return {
        leaveSettings: leaveSettingsResult.data,
        customEntitlements: customEntitlementsResult.data,
        leaveTypes: leaveTypesResult.data
      };
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useAttendanceSettings] Error:', err);
      setError(err.message);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId]);

  // Load data when company changes
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  /**
   * Update leave settings locally (for controlled inputs)
   */
  const updateLeaveSettings = useCallback((updates) => {
    setLeaveSettings(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Update custom entitlements list locally
   */
  const updateCustomEntitlementsList = useCallback((updater) => {
    setCustomEntitlements(updater);
  }, []);

  /**
   * Update leave types list locally
   */
  const updateLeaveTypesList = useCallback((updater) => {
    setLeaveTypes(updater);
  }, []);

  // Computed stats
  const stats = useMemo(() => {
    const systemLeaveTypes = leaveTypes.filter(t => t.is_system_default);
    const customLeaveTypes = leaveTypes.filter(t => !t.is_system_default);
    const adminOnlyTypes = leaveTypes.filter(t => t.required_permission === 'attendance.manage');
    const deductingTypes = leaveTypes.filter(t => t.deducts_from_balance);

    return {
      // Leave policy summary
      annualLeaveDays: leaveSettings.default_annual_leave,
      carryOverEnabled: leaveSettings.allow_carry_over,
      carryOverDays: leaveSettings.default_carry_over_days,
      bankHolidaysIncluded: leaveSettings.entitled_to_bank_holidays,

      // Leave types breakdown
      totalLeaveTypes: leaveTypes.length,
      systemLeaveTypes: systemLeaveTypes.length,
      customLeaveTypes: customLeaveTypes.length,
      adminOnlyTypes: adminOnlyTypes.length,
      deductingTypes: deductingTypes.length,

      // Custom entitlements
      totalCustomEntitlements: customEntitlements.length,
      totalDefaultEntitlementDays: customEntitlements.reduce(
        (sum, e) => sum + (e.default_days || 0),
        0
      )
    };
  }, [leaveSettings, leaveTypes, customEntitlements]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    leaveSettings,
    customEntitlements,
    leaveTypes,
    stats,

    // Loading state
    loading,
    error,

    // Local state updaters (for controlled inputs)
    updateLeaveSettings,
    updateCustomEntitlementsList,
    updateLeaveTypesList,

    // Actions
    refetch: loadData,

    // Constants for forms
    defaultNewEntitlement: DEFAULT_NEW_ENTITLEMENT,
    defaultNewLeaveType: DEFAULT_NEW_LEAVE_TYPE
  };
}

/**
 * Hook for attendance settings actions (save, add, update, delete)
 * Separated to keep concerns clean and reduce re-renders
 *
 * @param {string} companyId - Company ID
 * @param {Object} callbacks - Callbacks for state updates
 * @returns {Object} - Action functions and processing state
 */
export function useAttendanceSettingsActions(companyId, callbacks = {}) {
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);

  const {
    onLeaveSettingsSaved,
    onEntitlementAdded,
    onEntitlementUpdated,
    onEntitlementDeleted,
    onLeaveTypeAdded,
    onLeaveTypeUpdated,
    onLeaveTypeDeleted
  } = callbacks;

  /**
   * Save leave settings
   */
  const saveLeaveSettings = useCallback(async (settings) => {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    setSaving(true);

    try {
      const { error } = await supabase
        .from('company_leave_settings')
        .upsert({
          company_id: companyId,
          default_annual_leave: settings.default_annual_leave,
          default_sick_leave: settings.default_sick_leave,
          default_carry_over_days: settings.allow_carry_over ? settings.default_carry_over_days : 0,
          entitled_to_bank_holidays: settings.entitled_to_bank_holidays,
          working_days: settings.working_days,
          updated_date: new Date().toISOString()
        }, {
          onConflict: 'company_id'
        });

      if (error) throw error;

      if (onLeaveSettingsSaved) onLeaveSettingsSaved();
      return { success: true };
    } catch (err) {
      console.error('[useAttendanceSettingsActions] Save error:', err);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, [companyId, onLeaveSettingsSaved]);

  /**
   * Add custom entitlement type
   */
  const addCustomEntitlement = useCallback(async (entitlement, currentCount) => {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    if (!entitlement?.name?.trim()) {
      return { success: false, error: 'Name is required' };
    }

    setProcessing(true);

    try {
      const { data, error } = await supabase
        .from('custom_entitlement_types')
        .insert({
          company_id: companyId,
          name: entitlement.name.trim(),
          description: entitlement.description?.trim() || null,
          default_days: parseFloat(entitlement.default_days) || 0,
          display_order: currentCount || 0
        })
        .select()
        .single();

      if (error) throw error;

      if (onEntitlementAdded) onEntitlementAdded(data);
      return { success: true, data };
    } catch (err) {
      console.error('[useAttendanceSettingsActions] Add entitlement error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [companyId, onEntitlementAdded]);

  /**
   * Update custom entitlement type
   */
  const updateCustomEntitlement = useCallback(async (id, updates) => {
    if (!id) {
      return { success: false, error: 'Entitlement ID is required' };
    }

    setProcessing(true);

    try {
      const { data, error } = await supabase
        .from('custom_entitlement_types')
        .update({
          ...updates,
          updated_date: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (onEntitlementUpdated) onEntitlementUpdated(id, data);
      return { success: true, data };
    } catch (err) {
      console.error('[useAttendanceSettingsActions] Update entitlement error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onEntitlementUpdated]);

  /**
   * Delete custom entitlement type (soft delete)
   */
  const deleteCustomEntitlement = useCallback(async (id) => {
    if (!id) {
      return { success: false, error: 'Entitlement ID is required' };
    }

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('custom_entitlement_types')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      if (onEntitlementDeleted) onEntitlementDeleted(id);
      return { success: true };
    } catch (err) {
      console.error('[useAttendanceSettingsActions] Delete entitlement error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onEntitlementDeleted]);

  /**
   * Add leave type
   */
  const addLeaveType = useCallback(async (leaveType) => {
    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    if (!leaveType?.name?.trim()) {
      return { success: false, error: 'Name is required' };
    }

    setProcessing(true);

    try {
      const { data, error } = await supabase
        .from('leave_request_types')
        .insert({
          company_id: companyId,
          name: leaveType.name.trim(),
          description: leaveType.description?.trim() || null,
          color: leaveType.color || '#8B5CF6',
          icon: 'calendar',
          requires_approval: true,
          deducts_from_balance: true
        })
        .select()
        .single();

      if (error) throw error;

      if (onLeaveTypeAdded) onLeaveTypeAdded(data);
      return { success: true, data };
    } catch (err) {
      console.error('[useAttendanceSettingsActions] Add leave type error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [companyId, onLeaveTypeAdded]);

  /**
   * Update leave type
   */
  const updateLeaveType = useCallback(async (id, updates) => {
    if (!id) {
      return { success: false, error: 'Leave type ID is required' };
    }

    setProcessing(true);

    try {
      const { data, error } = await supabase
        .from('leave_request_types')
        .update({
          ...updates,
          updated_date: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (onLeaveTypeUpdated) onLeaveTypeUpdated(id, data);
      return { success: true, data };
    } catch (err) {
      console.error('[useAttendanceSettingsActions] Update leave type error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onLeaveTypeUpdated]);

  /**
   * Delete leave type (soft delete)
   */
  const deleteLeaveType = useCallback(async (id) => {
    if (!id) {
      return { success: false, error: 'Leave type ID is required' };
    }

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('leave_request_types')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      if (onLeaveTypeDeleted) onLeaveTypeDeleted(id);
      return { success: true };
    } catch (err) {
      console.error('[useAttendanceSettingsActions] Delete leave type error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onLeaveTypeDeleted]);

  return {
    // Actions
    saveLeaveSettings,
    addCustomEntitlement,
    updateCustomEntitlement,
    deleteCustomEntitlement,
    addLeaveType,
    updateLeaveType,
    deleteLeaveType,

    // State
    saving,
    processing
  };
}
